import { useCustomerAvatar } from "@expert/contentful";
import { useFeatures, type WorkspaceFeatures } from "@expert/features";
import {
    CallIntroFlow,
    CustomerVerification,
    CustomerView,
    LoadingAnimation,
    useCallIntroStore,
    useEligibility,
    useSalesContext,
    useVerificationFlowStore,
    useVerifiedCustomerStore,
} from "@expert/sales-tools";
import { Avatar, Container, Flex, Stack, Text } from "@mantine/core";
import clsx from "clsx";
import { useEffect } from "react";

import { useIVRInfo } from "@/customer-info/api";
import { useDigitalCompetencyListener } from "@/customer-panel/hooks/useDigitalCompetencyListener";
import { useEligibilityPrecheck } from "@/customer-panel/hooks/useEligibilityPrecheck";
import { useTaskMdn } from "@/customer-panel/hooks/useTaskMdn";
import { usePartnerConfig } from "@/partner/usePartnerConfig";
import { useSessionId, useTypedMostRecentTask } from "@/sdk";
import { useFeatureFlag } from "@/shared-utils";

import styles from "./SidecarTabPanel.module.css";

/**
 * Right-top "Customer" tab content (verification flow, then customer summary view). The profile
 * header is rendered once by `Sidecar` — do not duplicate it inside this tab.
 */
export function CustomerTab() {
    const { isChat, chatTask } = useTypedMostRecentTask();
    // Gate on the SalesContext taskType (built from the *active* task), not the
    // most-recent task. CustomerVerification throws when taskType is neither voice
    // nor chat, so this guard must read the same source — otherwise, once a call
    // ends (currentTask cleared but the task still lingers in the session),
    // useTypedMostRecentTask stays truthy, this renders, and the verification hook
    // crashes. Matches legacy CustomerPanel parity: no active task → render blank.
    const { taskType } = useSalesContext();
    const sessionId = useSessionId();
    const stepHistory = useVerificationFlowStore((state) => state.getStepHistory(sessionId));
    const callIntroStep = useCallIntroStore((state) => state.currentStep);

    const isCustomerFlowComplete = useVerifiedCustomerStore(
        (state) => state.getVerifiedCustomer(sessionId).isCustomerVerificationComplete,
    );
    const {
        features: { isEnhancedCustomerSearchEnabled },
        blocklistedNumbers = [],
        blocklistedPrefixes = [],
    } = usePartnerConfig();
    const {
        features: { isPreviousSessionSummaryEnabled },
    } = useFeatures<WorkspaceFeatures>();
    const { data: isChatDeviceDetectionEnabled = false } = useFeatureFlag<boolean>(
        "EWP-chat-isDeviceDetectionEnabled",
        {
            partner: chatTask?.partner,
            topic: chatTask?.topic,
            language: chatTask?.language,
        },
        { isEnabled: isChat },
    );

    useDigitalCompetencyListener();
    useEligibilityPrecheck(sessionId);

    const taskMdn = useTaskMdn({
        blocklistedNumbers,
        blocklistedPrefixes,
    });

    // Mirror verification completion into the verified customer store as a side effect of
    // step history changes (matches legacy CustomerPanel behavior).
    useEffect(() => {
        const isFlowComplete =
            callIntroStep === "IntroFlowCompleted" || stepHistory.at(stepHistory.length - 1) === "VerificationComplete";
        if (isFlowComplete !== isCustomerFlowComplete) {
            useVerifiedCustomerStore.getState().setIsCustomerVerificationComplete(sessionId, isFlowComplete);
        }
    }, [stepHistory, callIntroStep, isCustomerFlowComplete, sessionId]);

    const customerAvatarUrl = useCustomerAvatar(sessionId);
    const ivrInfo = useIVRInfo();
    const { isLoading: isEligibilityLoading } = useEligibility();

    if (!taskType.isVoice && !taskType.isChat) {
        return null;
    }

    return (
        <Flex pos="absolute" inset={0} className={styles.scrollableRoot} data-testid="customer-right-top-tab">
            <Flex className={styles.column}>
                {!isCustomerFlowComplete ? (
                    <div className={clsx(styles.fadeIn, styles.verificationForm)}>
                        {customerAvatarUrl && (
                            <Flex justify="center" pt="md" pb="xs">
                                <Avatar src={customerAvatarUrl} size={80} radius="xl" />
                            </Flex>
                        )}
                        {isEnhancedCustomerSearchEnabled ? (
                            <CallIntroFlow />
                        ) : (
                            <CustomerVerification
                                mdnFromTask={taskMdn}
                                // Remount when the IVR-provided name changes so the name field's
                                // useState initializer picks up freshly injected/handed-off data.
                                key={`${sessionId}-${ivrInfo?.customerName ?? ""}`}
                                isPreviousSessionSummaryEnabled={isPreviousSessionSummaryEnabled}
                            />
                        )}
                    </div>
                ) : isEligibilityLoading ? (
                    <Stack align="center" mt="sm" className={styles.fadeIn}>
                        <Text size="sm">Confirming customer information...</Text>
                        <Container maw="160px">
                            <LoadingAnimation />
                        </Container>
                    </Stack>
                ) : (
                    <Stack mt="sm" className={styles.fadeIn}>
                        <CustomerView hideCustomerInsights={isChat && !isChatDeviceDetectionEnabled} />
                    </Stack>
                )}
            </Flex>
        </Flex>
    );
}
